/**
 * Map Lite — the tech's stripped-down map: teammates' last ping (shift hours only, never home),
 * open work orders and in-progress inspections with a geocoded site, and the shop.
 * No addresses for people, no pricing. Data: GET /api/field-lookup/map (polled every 60 s).
 */
import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { RefreshCw } from "lucide-react";
import { format, parseISO } from "date-fns";
import { getFieldMap } from "@/lib/api";
import { PillRail, Pill } from "@/components/common/Segmented";
import { useI18n } from "@/i18n";

const STATE_LABEL = { at_site: "On site", en_route: "En route", en_route_shop: "To shop", at_shop: "At shop", stationary_off_site: "Off site", offsite: "Off site", offline: "No signal" };
const initials = (n = "") => n.trim().split(/\s+/).map(p => p[0]).join("").slice(0, 2).toUpperCase();

const techIcon = (t) => L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50%;background:${t.color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;font:700 9px system-ui;color:#fff" data-testid="map-lite-pin-tech-${t.tech_id}">${initials(t.name)}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -14],
});
const woIcon = (color = "#f97316", initials = "") => L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 32 42"><path d="M16 40 C16 40 30 22 30 14 A14 14 0 1 0 2 14 C2 22 16 40 16 40Z" fill="${color}" stroke="#fff" stroke-width="2"/>${initials ? `<text x="16" y="17.5" text-anchor="middle" font-family="system-ui" font-weight="800" font-size="9" fill="#fff">${initials}</text>` : `<circle cx="16" cy="14" r="5" fill="#fff"/>`}</svg>`,
  iconSize: [24, 32], iconAnchor: [12, 32], popupAnchor: [0, -28],
});
const inspIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="30" viewBox="0 0 32 42"><path d="M16 40 C16 40 30 22 30 14 A14 14 0 1 0 2 14 C2 22 16 40 16 40Z" fill="#0ea5e9" stroke="#fff" stroke-width="2"/><path d="M10 14 l4 4 l8 -8" stroke="#fff" stroke-width="3" fill="none"/></svg>`,
  iconSize: [22, 30], iconAnchor: [11, 30], popupAnchor: [0, -26],
});
const shopIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:5px;background:#10b981;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;font:800 10px system-ui;color:#fff">S</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -12],
});

const KIND_TAG = { contract: ["CON", "#2563eb", "#fff"], overhead: ["OH", "#F59E0B", "#0f172a"], warranty: ["WAR", "#D946EF", "#fff"], service: ["SER", "#1e293b", "#fff"] };
function StopLine({ s }) {
  const [tag, bg, fg] = KIND_TAG[s.job_kind] || KIND_TAG.service;
  return (
    <div className="border-t border-slate-100 pt-1 mt-1 first:border-0 first:pt-0 first:mt-0" data-testid={`map-lite-stop-${s.id}`}>
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center rounded-sm px-1 h-[12px] text-[7px] font-black" style={{ backgroundColor: bg, color: fg }}>{tag}</span>
        {s.wo_number && <span className="font-bold text-blue-600">{s.wo_number}</span>}
        {s.hours != null && <span className="text-slate-400 ml-auto">{s.hours}h</span>}
      </div>
      <div className="font-medium leading-tight">{s.title}</div>
      {(s.client_name || s.site_name) && <div className="text-slate-500">{s.client_name}{s.site_name && s.site_name !== s.client_name ? ` · ${s.site_name}` : ""}</div>}
    </div>
  );
}

function FitOnce({ points }) {
  const map = useMap();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (done || !points.length) return;
    map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [30, 30], maxZoom: 12 });
    setDone(true);
  }, [points, map, done]);
  return null;
}

export default function FieldMapLite() {
  const { t: tr } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [layers, setLayers] = useState({ team: true, wos: true, insps: true });
  const load = () => getFieldMap().then(r => { setData(r.data); setErr(""); }).catch(() => setErr("Could not load the map right now."));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const points = useMemo(() => {
    if (!data) return [];
    return [
      ...(layers.team ? data.team : []), ...(layers.wos ? data.open_work_orders : []), ...(layers.insps ? data.open_inspections : []),
      ...(data.shop ? [data.shop] : []),
    ].filter(p => p.lat != null && p.lng != null);
  }, [data, layers]);
  const center = data?.shop?.lat != null ? [data.shop.lat, data.shop.lng] : [39.74, -104.99];

  return (
    <div className="flex flex-col gap-2" data-testid="field-map-lite">
      <div className="flex flex-wrap items-center gap-2">
        <PillRail testId="map-lite-layers">
          <Pill on={layers.team} onClick={() => setLayers(l => ({ ...l, team: !l.team }))} testId="map-lite-layer-team">{tr("Team")} {data ? data.team.length : ""}</Pill>
          <Pill on={layers.wos} onClick={() => setLayers(l => ({ ...l, wos: !l.wos }))} testId="map-lite-layer-wos">{tr("This week's WOs")} {data ? data.open_work_orders.length : ""}</Pill>
          <Pill on={layers.insps} onClick={() => setLayers(l => ({ ...l, insps: !l.insps }))} testId="map-lite-layer-insps">{tr("Inspections")} {data ? data.open_inspections.length : ""}</Pill>
        </PillRail>
        <button type="button" onClick={load} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50" title={tr("Refresh")} data-testid="map-lite-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        <span className="text-[10px] text-slate-500 ml-auto" data-testid="map-lite-note">
          {data && !data.in_window ? tr("Team pins show during shift hours only") : tr("Team pins = last check-in · no home locations")}
        </span>
      </div>
      {err && <div className="text-xs text-red-600" data-testid="map-lite-error">{err}</div>}
      <div className="rounded-lg overflow-hidden" style={{ height: "calc(100vh - 210px)", minHeight: 320, position: "relative", zIndex: 0 }} data-testid="map-lite-container">
        <MapContainer center={center} zoom={9} className="w-full h-full" scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitOnce points={points} />
          {data?.shop && <Marker position={[data.shop.lat, data.shop.lng]} icon={shopIcon}><Popup><div className="text-xs font-semibold">{tr("Shop")}</div></Popup></Marker>}
          {layers.team && data?.team.map(t => (
            <Marker key={`t-${t.tech_id}`} position={[t.lat, t.lng]} icon={techIcon(t)}>
              <Popup>
                <div className="text-xs min-w-[170px] max-w-[220px]" data-testid={`map-lite-popup-tech-${t.tech_id}`}>
                  <div className="font-bold flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />{t.name}</div>
                  <div className="text-slate-600">{tr(STATE_LABEL[t.state] || STATE_LABEL.offline)}{t.site_name ? ` · ${t.site_name}` : ""}</div>
                  <div className="text-[10px] text-slate-400">{tr("checked in {m}m ago", { m: t.mins_since_ping })}</div>
                  <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tr("Today")}</div>
                  {(t.today_stops || []).length === 0 && <div className="text-slate-400 italic">{tr("No stops today")}</div>}
                  {(t.today_stops || []).map(s => <StopLine key={s.id} s={s} />)}
                </div>
              </Popup>
            </Marker>
          ))}
          {layers.wos && data?.open_work_orders.map(w => {
            const lead = (w.techs || [])[0];
            return (
            <Marker key={`w-${w.id}`} position={[w.lat, w.lng]} icon={woIcon(lead?.color, lead ? initials(lead.name) : "")}>
              <Popup>
                <div className="text-xs min-w-[160px]" data-testid={`map-lite-popup-wo-${w.id}`}>
                  <div className="font-bold text-blue-600">{w.wo_number}</div>
                  <div className="font-medium">{w.title}</div>
                  <div className="text-slate-500">{w.client_name}{w.site_name && w.site_name !== w.client_name ? ` · ${w.site_name}` : ""}{w.city ? ` · ${w.city}` : ""}</div>
                  <div className="text-[10px] text-slate-400">{(w.dates || []).map(x => format(parseISO(x), "EEE M/d")).join(" · ")}</div>
                  {(w.techs || []).length > 0 && (
                    <div className="flex flex-wrap gap-x-2 mt-1" data-testid={`map-lite-wo-techs-${w.id}`}>
                      {w.techs.map(t => <span key={t.id} className="inline-flex items-center gap-1 text-[10px] font-semibold"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />{t.name}</span>)}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
            );
          })}
          {layers.insps && data?.open_inspections.map(i => (
            <Marker key={`i-${i.id}`} position={[i.lat, i.lng]} icon={inspIcon}>
              <Popup>
                <div className="text-xs min-w-[160px]" data-testid={`map-lite-popup-insp-${i.id}`}>
                  <div className="font-bold text-sky-600">{i.template_name}{i.is_dot ? " · DOT" : ""}</div>
                  <div className="text-slate-500">{i.wo_number}{i.client_name ? ` · ${i.client_name}` : ""}{i.site_name && i.site_name !== i.client_name ? ` · ${i.site_name}` : ""}</div>
                  <div className="text-[10px] text-slate-400 capitalize">{(i.status || "").replace("_", " ")}</div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap" data-testid="map-lite-legend">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-slate-400 border border-white" />{tr("Teammate (tap for today's stops)")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" />{tr("WO this week · pin = assigned tech's color")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-sky-500" />{tr("Inspection")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />{tr("Shop")}</span>
      </div>
    </div>
  );
}
